import React from 'react';

import { notification } from 'antd';
import type { IntlShape } from 'react-intl';

import { isExpectedWorkJourneyMemberIssue } from '@src/util/sentry/workJourneyExpectedMemberError';

import type { WorkJourneyAssignedUser, WorkJourneyForm } from '../types';
import { buildWorkJourneyExpectedMemberIssueDescription } from './workJourneyExpectedMemberIssueNotify';
import {
  validateWorkJourneyForm,
  type WorkJourneyValidationResult,
  type WorkJourneyValidationTranslations,
} from './validateWorkJourneyForm';

const VALIDATION_PREFIX = 'workJourney.validation';
const MISSING_ADDRESS_MESSAGE_ID = 'users.select.tooltip.missingAddress';
const MEMBERS_PENDING_MESSAGE_ID = 'users.select.tooltip.pendingUser';
const MEMBERS_INACTIVE_MESSAGE_ID = 'users.select.tooltip.inactiveUser';

export type WorkJourneySaveErrorNotifyOptions = {
  messageId?: string;
  members?: WorkJourneyAssignedUser[];
  matchUrl?: string;
};

export class WorkJourneyValidationError extends Error {
  errors: string[];

  constructor(errors: string[]) {
    super(errors[0] ?? 'Validation failed');
    this.name = 'WorkJourneyValidationError';
    this.errors = errors;
  }
}

export const renderWorkJourneyValidationErrorsList = (errors: string[]) => (
  <ul style={{ margin: 0, paddingLeft: 20 }}>
    {errors.map((error, index) => (
      <li key={`${index}-${error}`}>{error}</li>
    ))}
  </ul>
);

export const buildWorkJourneyValidationTranslations = (
  intl: IntlShape,
): WorkJourneyValidationTranslations => ({
  nameRequired: intl.formatMessage({ id: `${VALIDATION_PREFIX}.nameRequired` }),
  workShiftRequired: intl.formatMessage({ id: `${VALIDATION_PREFIX}.workShiftRequired` }),
  workShiftIdRequired: intl.formatMessage({ id: `${VALIDATION_PREFIX}.workShiftIdRequired` }),
  startDateRequired: intl.formatMessage({ id: `${VALIDATION_PREFIX}.startDateRequired` }),
  supervisorsRequired: intl.formatMessage({ id: `${VALIDATION_PREFIX}.supervisorsRequired` }),
  invalidRequiredMembers: intl.formatMessage({ id: `${VALIDATION_PREFIX}.invalidRequiredMembers` }),
  locationRequired: intl.formatMessage({ id: `${VALIDATION_PREFIX}.locationRequired` }),
  locationTypeRequired: intl.formatMessage({ id: `${VALIDATION_PREFIX}.locationTypeRequired` }),
  breakMarkingTypeRequired: intl.formatMessage({
    id: `${VALIDATION_PREFIX}.breakMarkingTypeRequired`,
  }),
  breakStartTimeRequired: intl.formatMessage({ id: `${VALIDATION_PREFIX}.breakStartTimeRequired` }),
  toleranceTimeInvalid: intl.formatMessage({ id: `${VALIDATION_PREFIX}.toleranceTimeInvalid` }),
  earlyEndTimeInvalid: intl.formatMessage({ id: `${VALIDATION_PREFIX}.earlyEndTimeInvalid` }),
  shiftEndDateBeforeStart: intl.formatMessage({
    id: `${VALIDATION_PREFIX}.shiftEndDateBeforeStart`,
  }),
  journeyTypeRequired: intl.formatMessage({ id: `${VALIDATION_PREFIX}.journeyTypeRequired` }),
  workCircumstancesRequired: intl.formatMessage({
    id: `${VALIDATION_PREFIX}.workCircumstancesRequired`,
  }),
  membersMissingAddress: intl.formatMessage({ id: MISSING_ADDRESS_MESSAGE_ID }),
  membersPending: intl.formatMessage({ id: MEMBERS_PENDING_MESSAGE_ID }),
  membersInactive: intl.formatMessage({ id: MEMBERS_INACTIVE_MESSAGE_ID }),
});

export const runWorkJourneyValidation = (
  form: WorkJourneyForm,
  intl: IntlShape,
): WorkJourneyValidationResult =>
  validateWorkJourneyForm(form, buildWorkJourneyValidationTranslations(intl));

export const notifyWorkJourneyValidationErrors = (
  errors: string[],
  intl: IntlShape,
  messageId = 'workJourney.validation.failed',
): void => {
  if (errors.length === 0) {
    return;
  }

  notification.error({
    message: intl.formatMessage({ id: messageId }),
    description: renderWorkJourneyValidationErrorsList(errors),
  });
};

export const getWorkJourneySaveErrorDescription = (
  error: unknown,
  intl: IntlShape,
  options: Omit<WorkJourneySaveErrorNotifyOptions, 'messageId'> = {},
): React.ReactNode => {
  const expectedDescription = buildWorkJourneyExpectedMemberIssueDescription(
    error,
    intl,
    options,
  );
  if (expectedDescription != null) {
    return expectedDescription;
  }
  return error instanceof Error ? error.message : String(error);
};

export const notifyWorkJourneySaveError = (
  error: unknown,
  intl: IntlShape,
  messageIdOrOptions: string | WorkJourneySaveErrorNotifyOptions = 'workJourney.errorCreating',
): void => {
  if (error instanceof WorkJourneyValidationError) {
    notifyWorkJourneyValidationErrors(error.errors, intl);
    return;
  }

  const options: WorkJourneySaveErrorNotifyOptions =
    typeof messageIdOrOptions === 'string'
      ? { messageId: messageIdOrOptions }
      : messageIdOrOptions;

  const isListedMemberIssue = isExpectedWorkJourneyMemberIssue(error);

  notification.error({
    message: intl.formatMessage({ id: options.messageId ?? 'workJourney.errorCreating' }),
    description: getWorkJourneySaveErrorDescription(error, intl, options),
    duration: isListedMemberIssue ? 8 : 4.5,
  });
};
